// src/context/NotificationContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type NotificationType = 'success' | 'error' | 'info';

interface NotificationState {
    showNotification: (message: string, type?: NotificationType) => void;
    showConfirmation: (messageKey: string, onConfirm: () => void) => void;
}

const NotificationContext = createContext<NotificationState | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

const NotificationToast = ({ message, type, onClose }: { message: string; type: NotificationType; onClose: () => void }) => {
    const baseClasses = "fixed top-5 right-5 max-w-sm w-full bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden z-50";
    const typeClasses = {
        success: 'border-green-500',
        error: 'border-red-500',
        info: 'border-blue-500',
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 5000); // Powiadomienie znika po 5 sekundach
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className={`${baseClasses} border-l-4 ${typeClasses[type]}`}>
            <div className="p-4">
                <div className="flex items-start">
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                        <p className="text-sm font-medium text-white">{message}</p>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex">
                        <button onClick={onClose} className="bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            <span className="sr-only">Close</span>
                            &times;
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ConfirmationDialog = ({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                <p className="text-white text-lg mb-6">{message}</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onCancel} className="px-4 py-2 rounded-md text-white bg-gray-600 hover:bg-gray-500 transition-colors">Anuluj</button>
                    <button onClick={onConfirm} className="px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors">Potwierdź</button>
                </div>
            </div>
        </div>
    );
};

// --- Dostawca Kontekstu (Provider) ---
export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [notification, setNotification] = useState<{ message: string; type: NotificationType; key: number } | null>(null);
    const [confirmation, setConfirmation] = useState<{ message: string; onConfirm: () => void; isVisible: boolean } | null>(null);
    const { t } = useTranslation();

    const showNotification = useCallback((message: string, type: NotificationType = 'info') => {
        setNotification({ message, type, key: Date.now() });
    }, []);

    const showConfirmation = useCallback((messageKey: string, onConfirm: () => void) => {
        const message = t(messageKey);
        setConfirmation({ message, onConfirm, isVisible: true });
    }, [t]);

    const handleConfirm = () => {
        if (confirmation) {
            confirmation.onConfirm();
            setConfirmation(null);
        }
    };

    const handleCancel = () => {
        setConfirmation(null);
    };

    const value = { showNotification, showConfirmation };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {notification && <NotificationToast key={notification.key} message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
            {confirmation && <ConfirmationDialog message={confirmation.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
        </NotificationContext.Provider>
    );
};
